import { TEMPLATES_TYPES } from 'components/landing/constants';
import { useMemo } from 'react';
import { usePageInfo } from 'context/PageInfoContext';
import { useTranslations } from 'next-intl';
/**
 * # useRichTranslation
 *  Custom Hook that implements the useTranslations form next-intl,
 *  and define all common variables inside, with the rich method.
 * @param {string} namespace - Namespace of the i18n JSON message
 * @param {object} extraProps - Other props to be added to the rich() method, as variables or other tags
 * @returns
 */

const getInsuranceCarKeyword = (deepLevel, env) => {
    if (deepLevel === 1) {
        return env.base_keyword;
    } else if (deepLevel === 2) {
        return env.car_brand;
    } else {
        return env.car_name;
    }
};

const getKeyword = (template, deepLevel, env) => {
    if (template === TEMPLATES_TYPES.INSURANCE_CAR) {
        return getInsuranceCarKeyword(deepLevel, env);
    } else if (template === TEMPLATES_TYPES.INSURANCE_LOCATION) {
        return env.place_name;
    } else {
        return '';
    }
};

const getCarName = pageConfig => {
    return pageConfig.env.car_name || pageConfig.car_name || pageConfig.external_car_name || '';
};

const getCarBrand = pageConfig => {
    return pageConfig.env.car_brand || pageConfig.car_brand_name || '';
};

const getCompanyName = pageConfig => {
    return pageConfig.env.company_name || pageConfig.company_name || '';
};

const getPlaceName = pageConfig => {
    return pageConfig.env.place_name || pageConfig.place_name || '';
};

const getKeywordCompanyAndPlace = pageConfig => {
    return pageConfig.env.keyword_company_and_place || pageConfig.env.company_name || pageConfig.company_name || '';
};

export default function useRichTranslation (namespace) {
    const tBase = useTranslations(namespace);
    const { pageInfo } = usePageInfo();

    const data = useMemo(() => {
        const keyword = getKeyword(
            pageInfo.template_type,
            pageInfo.page_context.deep_level,
            pageInfo.page_context.page_config.env
        );
        const seoKeyword = pageInfo.seo_keyword || 'auto';
        const carName = getCarName(pageInfo.page_context.page_config);
        const companyName = getCompanyName(pageInfo.page_context.page_config);
        const keywordCompanyAndPlace = getKeywordCompanyAndPlace(pageInfo.page_context.page_config);
        const carBrand = getCarBrand(pageInfo.page_context.page_config);
        const placeName = getPlaceName(pageInfo.page_context.page_config);

        return (key, extraProps = {}) => {
            return tBase.rich(key, {
                ...extraProps,
                keyword,
                seoKeyword,
                car_name: carName,
                company_name: companyName,
                keyword_company_and_place: keywordCompanyAndPlace,
                car_brand: carBrand,
                place_name: placeName,
                important: (children) => <strong>{children}</strong>,
                list: (children) => <ol>{children}</ol>,
                item: (children) => <li>{children}</li>,
                br: (children) => (
                    <>
                        <br />
                        {children}
                    </>
                ),
                p: (children) => <p>{children}</p>
            });
        };
    }, [namespace, pageInfo.page_context.code]);
    return data;
}
